import React, { useState } from "react";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { config } from "../config";
import { useAuthStore } from "../auth/store";
import { WorkoutPayload } from "./workouts/types";
import Workout from "./workouts/Workout";
import {
  Card,
  PrimaryButton,
  SecondaryButton,
  TextArea,
  Label,
  HelperText,
  Muted,
  StatusPill,
  InlineCode,
} from "./ui";

const Layout = styled.section`
  display: grid;
  gap: 2rem;
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 1080px) {
    grid-template-columns: 1.1fr 0.95fr;
    align-items: start;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;
  margin-top: 1.5rem;
`;

const StatusText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #334155;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const PreviewEmpty = styled(Muted)`
  padding: 1.75rem 1.25rem;
  border-radius: 18px;
  border: 1px dashed rgba(148, 163, 184, 0.4);
  text-align: center;
`;

const GeneratePage: React.FC = () => {
  const [text, setText] = useState("");
  const [statusMsg, setStatusMsg] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [workout, setWorkout] = useState<WorkoutPayload | null>(null);

  const {
    state: { isAuthenticated, isConnected },
  } = useAuthStore();

  const navigate = useNavigate();

  const handleGenerate = async () => {
    if (!isAuthenticated) {
      setStatusMsg("Create a LaceAI account before generating workouts.");
      navigate("/connect");
      return;
    }

    setIsGenerating(true);
    setStatusMsg("Generating structured workout...");
    try {
      const res = await fetch(`${config.REACT_APP_API_BASE_URL}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text }),
      });

      const contentType = res.headers.get("content-type") || "";
      const isJson = contentType.includes("application/json");
      const data = isJson ? await res.json() : null;

      if (!res.ok) {
        const fallbackBody = data ? "" : (await res.text()).trim();
        const errorMessage =
          (data as { error?: string } | null)?.error ||
          fallbackBody ||
          `Generation failed (HTTP ${res.status}).`;
        setStatusMsg(errorMessage);
        return;
      }

      if (!data || typeof data !== "object") {
        setStatusMsg("Generation failed: server returned an invalid response.");
        return;
      }

      setWorkout(data as WorkoutPayload);
      setStatusMsg("Workout generated. Review and upload when ready.");
    } catch (e) {
      console.error(e);
      const message =
        e instanceof Error
          ? e.message
          : "Check your connection and API URL, then try again.";
      setStatusMsg(`We hit a snag generating the workout. ${message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpload = async () => {
    if (!isConnected) {
      setStatusMsg("Link your Garmin account before uploading this workout.");
      navigate("/connect");
      return;
    }

    setIsUploading(true);
    setStatusMsg("Uploading workout to Garmin...");
    try {
      if (!workout) {
        setStatusMsg("Generate a workout before uploading.");
        return;
      }
      const res = await fetch(`${config.REACT_APP_API_BASE_URL}/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(workout),
      });

      if (!res.ok) {
        const textErr = await res.text();
        throw new Error(textErr);
      }

      setStatusMsg("Upload successful. Check your Garmin calendar to schedule it.");
    } catch (e) {
      console.error(e);
      setStatusMsg("Upload failed. Please try generating the workout again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Layout>
      <Column>
        <Card>
          <BadgeRow>
            <StatusPill>{isConnected ? "Garmin connected" : "Ready to generate"}</StatusPill>
            <Muted>
              {isConnected
                ? "You can generate workouts instantly. Describe your run and LaceAI handles the structured steps."
                : "Generate and review workouts now. Link Garmin only when you're ready to upload one."}
            </Muted>
          </BadgeRow>
        </Card>

        <Card>
          <div>
            <Label htmlFor="workout-text">Describe your workout</Label>
            <TextArea
              id="workout-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="15 min warmup, 6x800m @ 3:45/km with 90s jog, 10 min cool down"
              disabled={isGenerating}
            />
            <HelperText>
              Mention reps, recoveries, paces, or zones. You can even nest repeats like
              {" "}
              <InlineCode>3 x (4 x 200m fast, 200m jog)</InlineCode>.
            </HelperText>
          </div>

          <Actions>
            <PrimaryButton
              onClick={handleGenerate}
              disabled={!text.trim() || isGenerating}
            >
              {isGenerating ? "Generating..." : "Generate Workout"}
            </PrimaryButton>
            {workout && (
              <SecondaryButton onClick={handleUpload} disabled={isUploading}>
                {isUploading
                  ? "Uploading..."
                  : isConnected
                    ? "Upload to Garmin"
                    : "Connect Garmin to Upload"}
              </SecondaryButton>
            )}
            {statusMsg && <StatusText>{statusMsg}</StatusText>}
          </Actions>
        </Card>
      </Column>

      <Column>
        <Card>
          {workout ? (
            <Workout workout={workout} />
          ) : (
            <PreviewEmpty>
              Generate a plan to see the breakdown of steps, repeats, and targets exactly as they&rsquo;ll appear on Garmin.
            </PreviewEmpty>
          )}
        </Card>
      </Column>
    </Layout>
  );
};

export default GeneratePage;
